import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { QuizApiService } from '@/services/QuizApiService';
import { 
  TestType, 
  Question, 
  AdaptiveState, 
  SimpleAdaptiveState,
  SessionProgress,
  QuizSummary,
  SessionConfig,
  SessionBlockIdentity
} from '@/types/adaptive';
import { logger } from '@/utils/logger';
import { isDailyLimitError, type DailyFreeAllowance } from '@/utils/freeTier';
import { todaysSessionKeys } from '@/hooks/queries/useTodaysSession';

// Query keys for consistent caching
export const quizSessionKeys = {
  all: ['quiz-sessions'] as const,
  session: (sessionId: string) => [...quizSessionKeys.all, sessionId] as const,
  results: (sessionId: string) => [...quizSessionKeys.all, sessionId, 'results'] as const,
} as const;

// Types for our modern API
export interface QuizSessionData extends SessionBlockIdentity {
  sessionId: string;
  testType: string;
  currentQuestion: Question | null;
  currentQuestionNote: string | null; // 🆕 Question notes from original
  adaptiveState: SimpleAdaptiveState | AdaptiveState;
  sessionProgress: SessionProgress;
  sessionConfig: SessionConfig;
  sessionAskedQuestions: string[]; // 🆕 Track asked questions from original
  isComplete: boolean;
  sessionComplete?: boolean; // 🆕 Backend completion signal
  waitingForBackend?: boolean; // 🆕 Flag for when adaptive algorithm is determining next question
  finalSummary?: QuizSummary;
  nextQuestion: Question | null; // 🆕 Next question from original
  nextQuestionNote: string | null; // 🆕 Next question note from original
  /** Metered free-tier remaining allowance (present only for capped free users). */
  dailyFree?: DailyFreeAllowance;
  /**
   * The backend withheld the follow-up question because today's free allowance
   * is spent (`answerQuestion` answers 200 with `dailyLimitReached: true` and no
   * `nextQuestion`). Without this the client cannot tell "cap hit" from "the
   * adaptive engine is still thinking" and parks the user on the
   * "Choosing your next question" spinner forever.
   */
  dailyLimitReached?: boolean;
  /**
   * The focus categories this session was started with (category drill from
   * "Your next wins" / Progress weak areas). CLIENT-derived: seeded from the
   * initializeSession params and carried across loadSession refetches —
   * loadQuizSession doesn't return the session doc's `categories` field
   * (its sessionConfig.focusAreas reads a field that is never written), so a
   * hard refresh mid-session gracefully falls back to the testType title.
   */
  categories?: string[];
}

interface InitializeSessionParams {
  testType: TestType;
  /** Backend-owned session shape: practice/category = endless, mock = blueprint length. */
  sessionMode?: 'practice' | 'category' | 'mock';
  targetQuestionCount?: number;
  categories?: string[]; // For focused practice on specific categories
  startQuestionId?: string; // Deep-link: start on this exact question (email "try this question" CTA)
}

interface SubmitAnswerParams {
  sessionId: string;
  questionId: string;
  selectedAnswer: string;
  timeSpent: number;
  testType: string;
  // NEW: Enhanced metadata for backend contract alignment
  metadata?: {
    hintsUsed?: number;
    confidence?: number;
    timeSpentReading?: number;
    flaggedForReview?: boolean;
  };
  questionFeedback?: { feedback: 'positive' | 'negative'; timestamp: Date };
}

/** Contract shape — see PauseSessionRequest in QuizApiService (#330). */
interface PauseSessionParams {
  sessionId: string;
  currentState?: {
    currentQuestionIndex?: number;
    score?: number;
  };
  pauseReason?: string;
}

interface ResumeSessionParams {
  sessionId: string;
  resumeToken: string;
}

/**
 * Hook to fetch a quiz session by ID
 * Automatically handles completed sessions by loading results
 * 🆕 Enhanced with retry logic and better error handling from original
 */
export const useQuizSession = (sessionId: string | null) => {
  const quizService = QuizApiService.getInstance();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: quizSessionKeys.session(sessionId || ''),
    queryFn: async (): Promise<QuizSessionData> => {
      if (!sessionId) {
        throw new Error('Session ID is required');
      }

      // loadSession doesn't return the session's focus categories — carry them
      // over from the cached entry (seeded at init) so a refetch mid-session
      // doesn't flip a "{Category} · Focus Session" title back to the exam title.
      const prior = queryClient.getQueryData<QuizSessionData>(quizSessionKeys.session(sessionId));

      // Single attempt — react-query's `retry` policy (below) handles transient
      // retries. The previous inner 3x loop MULTIPLIED with that policy, so a
      // failing load could fire loadSession up to 9 times (each possibly with a
      // CORS→HTTP fallback). loadSession is also bounded by callWithTimeout now.
      try {
        const sessionData = await quizService.loadSession({ sessionId });
        return {
          sessionId: sessionData.sessionId,
          testType: sessionData.testType,
          currentQuestion: sessionData.currentQuestion,
          currentQuestionNote: sessionData.currentQuestionNote || null,
          adaptiveState: sessionData.adaptiveState,
          sessionProgress: sessionData.sessionProgress,
          sessionConfig: sessionData.sessionConfig,
          sessionAskedQuestions: sessionData.sessionAskedQuestions || [],
          isComplete: false,
          sessionType: sessionData.sessionType,
          trialBlockId: sessionData.trialBlockId,
          block: sessionData.block,
          timeLimitSeconds: sessionData.timeLimitSeconds,
          timeRemaining: sessionData.timeRemaining,
          nextQuestion: sessionData.nextQuestion || null,
          nextQuestionNote: sessionData.nextQuestionNote || null,
          dailyFree: sessionData.dailyFree,
          categories: prior?.categories,
          // A fresh load reflects server truth: any cap-hit flag from the
          // previous answer in this tab is stale.
          dailyLimitReached: false,
        };
      } catch (error) {
        // A completed session surfaces as an error from loadSession — that's not
        // a failure, load the results view instead. Anything else re-throws so
        // react-query's retry policy decides.
        const errorMessage = error instanceof Error ? error.message : String(error);
        const errorCode = (error as { code?: string })?.code;
        const isCompletedError = errorMessage.includes('completed') ||
                                errorCode === 'functions/failed-precondition' ||
                                errorCode === 'failed-precondition';

        if (!isCompletedError) {
          throw error;
        }

        logger.info('useQuizSession', 'Session completed, loading results', { sessionId });
        const results = await quizService.getQuizResults(sessionId);

        const fallbackConfig: SessionConfig = {
          targetQuestions: results.targetQuestions,
          estimatedDuration: results.targetQuestions === null ? null : results.targetQuestions * 2,
          focusAreas: []
        };

        const sessionAskedQuestions = Array.isArray(results.sessionAskedQuestions)
          ? results.sessionAskedQuestions
          : [];

        return {
          sessionId,
          testType: results.testType ?? '',
          currentQuestion: null,
          currentQuestionNote: null,
          adaptiveState: results.adaptiveState,
          sessionProgress: {
            questionsAnswered: results.currentQuestionIndex,
            totalQuestions: results.targetQuestions,
            currentScore: results.score,
            timeElapsed: results.timeSpent,
            wrongAnswers: results.wrongAnswers || []
          },
          sessionConfig: results.sessionConfig || fallbackConfig,
          sessionAskedQuestions,
          isComplete: true,
          sessionType: results.sessionType,
          trialBlockId: results.trialBlockId,
          block: results.block,
          finalSummary: results.finalSummary,
          nextQuestion: null,
          nextQuestionNote: null,
          categories: prior?.categories,
        };
      }
    },
    enabled: !!sessionId,
    staleTime: 1000 * 60 * 2, // 2 minutes - sessions change frequently
    gcTime: 1000 * 60 * 10, // 10 minutes
    retry: (failureCount, error) => {
      // Don't retry terminal states: not-found or completed (checked by code
      // first, message as a fallback). Everything else gets up to 2 retries.
      // A hit daily cap is a terminal, expected answer — retrying it just
      // delays the paywall by two more round trips.
      if (isDailyLimitError(error)) return false;
      const errorMessage = error instanceof Error ? error.message : String(error);
      const errorCode = (error as { code?: string })?.code;
      if (
        errorCode === 'functions/not-found' ||
        errorCode === 'functions/failed-precondition' ||
        errorMessage.includes('not found') ||
        errorMessage.includes('completed')
      ) {
        return false;
      }
      return failureCount < 2;
    },
  });
};

/**
 * Hook to initialize a new quiz session
 */
export const useInitializeSession = () => {
  const queryClient = useQueryClient();
  const quizService = QuizApiService.getInstance();

  return useMutation({
    mutationFn: async (params: InitializeSessionParams): Promise<QuizSessionData> => {
      logger.debug('useInitializeSession', 'Initializing session', params);

      // Session shape is backend policy now (sessionModes.ts): the client
      // sends a MODE, never a count. Callers that still pass legacy
      // targetQuestionCount are ignored here on purpose - practice is endless.
      const sessionMode = params.sessionMode
        ?? (params.categories && params.categories.length > 0 ? 'category' : 'practice');
      const response = await quizService.initializeSession({
        testType: params.testType,
        sessionMode,
        categories: params.categories, // Pass categories for focused practice
        ...(params.startQuestionId && { startQuestionId: params.startQuestionId }) // Deep-link to a specific question
      });

      return {
        sessionId: response.sessionId,
        testType: params.testType,
        currentQuestion: response.firstQuestion,
        currentQuestionNote: null, // 🆕 Reset note for new session
        adaptiveState: response.adaptiveState,
        sessionProgress: {
          questionsAnswered: 0,
          totalQuestions: response.sessionConfig.targetQuestions,
          currentScore: 0,
          timeElapsed: 0,
          wrongAnswers: []
        },
        sessionConfig: response.sessionConfig,
        sessionAskedQuestions: response.firstQuestion?.id ? [response.firstQuestion.id] : [], // 🆕 Track first question
        isComplete: false,
        sessionComplete: false, // Explicitly set for consistency
        waitingForBackend: false, // Initial state - not waiting
        nextQuestion: null,
        nextQuestionNote: null,
        dailyFree: response.dailyFree,
        categories: params.categories,
        sessionType: response.sessionType,
        trialBlockId: response.trialBlockId,
        block: response.block,
        timeLimitSeconds: response.timeLimitSeconds,
      };
    },
    onSuccess: (data) => {
      // Cache the new session
      queryClient.setQueryData(
        quizSessionKeys.session(data.sessionId),
        data
      );
      
      logger.debug('useInitializeSession', 'Session initialized successfully', {
        sessionId: data.sessionId
      });
    },
    onError: (error) => {
      logger.error('useInitializeSession', 'Failed to initialize session', { error });
    },
  });
};

/**
 * Hook to submit an answer
 * 🆕 Enhanced with question notes and asked questions tracking
 */
export const useSubmitAnswer = () => {
  const queryClient = useQueryClient();
  const quizService = QuizApiService.getInstance();

  return useMutation({
    mutationFn: async (params: SubmitAnswerParams) => {
      logger.debug('useSubmitAnswer', 'Submitting answer', {
        sessionId: params.sessionId,
        questionId: params.questionId,
        timeSpent: params.timeSpent
      });

      const response = await quizService.submitAnswer(params);
      return response;
    },
    onSuccess: (response, variables) => {
      // Update the session cache with new state
      queryClient.setQueryData(
        quizSessionKeys.session(variables.sessionId),
        (oldData: QuizSessionData | undefined): QuizSessionData | undefined => {
          if (!oldData) return undefined;

          return {
            ...oldData,
            adaptiveState: response.adaptiveState,
            sessionProgress: response.sessionProgress,
            isComplete: response.sessionComplete,
            sessionComplete: response.sessionComplete, // Ensure both fields are set for compatibility
            finalSummary: response.finalSummary,
            // 🆕 Enhanced state management from original
            currentQuestion: oldData.currentQuestion, // Keep showing same question until feedback hidden
            nextQuestion: response.nextQuestion || null,
            nextQuestionNote: response.nextQuestionNote || null,
            // 🆕 Track asked questions
            sessionAskedQuestions: response.nextQuestion 
              ? [...oldData.sessionAskedQuestions, response.nextQuestion.id]
              : oldData.sessionAskedQuestions,
            // 🚨 CRITICAL FIX: Set waiting state when no next question but not complete
            waitingForBackend: !response.nextQuestion && !response.sessionComplete,
            // Metered free tier: refresh remaining allowance from each answer.
            dailyFree: response.dailyFree ?? oldData.dailyFree,
            // Cap hit on THIS answer: the answer was recorded, the follow-up was
            // withheld. Carried so the quiz renders the paywall instead of the
            // indefinite "Choosing your next question" state above.
            dailyLimitReached: response.dailyLimitReached === true,
          };
        }
      );

      // The Today's Session verdict is cached for 5 minutes, so a finished
      // session would still read as "resume" on /practice inside that window.
      if (response.sessionComplete) {
        void queryClient.invalidateQueries({ queryKey: todaysSessionKeys.all });
      }

      logger.debug('useSubmitAnswer', 'Answer submitted successfully', {
        isCorrect: response.isCorrect,
        sessionComplete: response.sessionComplete
      });
    },
    onError: (error) => {
      logger.error('useSubmitAnswer', 'Failed to submit answer', { error });
    },
  });
};

/**
 * 🆕 Hook to pause a quiz session (from original QuizSessionContext)
 */
export const usePauseSession = () => {
  const queryClient = useQueryClient();
  const quizService = QuizApiService.getInstance();

  return useMutation({
    // The global QueryClient default is `mutations: { retry: 1 }` (App.tsx).
    // On a timed-out pause that would double the worst-case wait behind the
    // exit click for a call that may already have landed server-side.
    retry: 0,
    mutationFn: async (params: PauseSessionParams) => {
      logger.debug('usePauseSession', 'Pausing session', {
        sessionId: params.sessionId,
        reason: params.pauseReason
      });

      const response = await quizService.pauseSession(params);
      return response;
    },
    onSuccess: (response, variables) => {
      // Store resume token and expiry in localStorage
      const resumeTokenKey = `resumeToken_${variables.sessionId}`;
      const expiryKey = `resumeExpiry_${variables.sessionId}`;

      localStorage.setItem(resumeTokenKey, response.resumeToken);
      localStorage.setItem(expiryKey, response.expiresAt);

      logger.debug('usePauseSession', 'Resume token and expiry stored', {
        sessionId: variables.sessionId,
        expiresAt: response.expiresAt
      });

      // Remove session from cache when paused
      queryClient.removeQueries({
        queryKey: quizSessionKeys.session(variables.sessionId)
      });

      void queryClient.invalidateQueries({ queryKey: todaysSessionKeys.all });

      logger.debug('usePauseSession', 'Session paused successfully');
    },
    onError: (error) => {
      logger.error('usePauseSession', 'Failed to pause session', { error });
    },
  });
};

/*
 * NOTE (2026-07-27 restore-path unification): useResumeSession was deleted —
 * it had ZERO importers. Web restores every session (fresh nav, refresh,
 * paused, email deep-link) through the loadQuizSession query above; the
 * resume callable is the iOS paused-session path.
 */

/**
 * Hook to get quiz results for a completed session
 */
export const useQuizResults = (sessionId: string | null) => {
  const quizService = QuizApiService.getInstance();

  return useQuery({
    queryKey: quizSessionKeys.results(sessionId || ''),
    queryFn: async () => {
      if (!sessionId) {
        throw new Error('Session ID is required');
      }
      return await quizService.getQuizResults(sessionId);
    },
    enabled: !!sessionId,
    staleTime: 1000 * 60 * 10, // 10 minutes - results don't change
    gcTime: 1000 * 60 * 60, // 1 hour
  });
};